import { useEffect, useRef, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import NameField from 'components/forms/new/formikFormFields/NameField';
import NumberField from 'components/forms/new/formikFormFields/NumberField';
import DropDown from 'components/forms/new/formikFormFields/DropDown';
import { ITEM_CODE_OPTIONS, calculateLineTotal } from '../constants';

const itemCodeData = ITEM_CODE_OPTIONS.map((option) => ({
    id: option.value,
    name: option.label,
}));

// Compact-width override for the shared field components, which default to
// form-page widths (240px+) that are too wide for a spreadsheet-style column.
// Height matches the DropDown control height (30px, from dropDownFormStyle)
// so every cell in the row lines up.
const compactFieldSx = {
    '& input': {
        minWidth: 'unset !important',
        width: '100% !important',
        height: '30px !important',
    },
};

// NameField (unlike NumberField/DropDown) always applies a hardcoded 15px
// top padding with no way to opt out, which pushes Description out of line
// with the other cells - cancel it out here.
const descriptionFieldSx = {
    ...compactFieldSx,
    marginTop: '-15px',
};

// Editable Item Code Cell (Dropdown) - Last in flow
const EditableItemCodeCell = ({ getValue, row, column, table }) => {
    const initValue = getValue();
    const { darkMode } = useSelector((state) => state.settings);
    const { updateData, focusedRow, setFocusedRow } = table.options.meta;
    const [value, setValue] = useState(initValue);
    const containerRef = useRef(null);

    const handleSelectChange = (item) => {
        const newValue = item?.id || '';
        setValue(newValue);
        updateData(row.index, column.id, newValue);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            setFocusedRow(row.index, 'description');
        }
    };

    useEffect(() => {
        if (row.index === focusedRow.row && focusedRow.column === 'itemCode') {
            containerRef?.current?.querySelector('input')?.focus();
        }
    }, [focusedRow, row.index]);

    useEffect(() => {
        setValue(initValue);
    }, [initValue]);

    return (
        <Box ref={containerRef} width="190px">
            <DropDown
                field={{ name: column.id, value }}
                name={column.id}
                data={itemCodeData}
                shownValue="name"
                placeholder="- Select -"
                handleSelectChange={handleSelectChange}
                onKeyDown={onKeyDown}
                darkMode={darkMode}
                noPadding
                width="190px"
                minWidth="190px"
            />
        </Box>
    );
};

// Editable Quantity Cell (Number Input)
const EditableQuantityCell = ({ getValue, row, column, table }) => {
    const initValue = getValue();
    const { darkMode } = useSelector((state) => state.settings);
    const { updateData, focusedRow, setFocusedRow, isAddingRowRef } =
        table.options.meta;
    const [value, setValue] = useState(initValue);
    const inputRef = useRef(null);

    const onFocus = () => {
        setFocusedRow(row.index, 'quantity');
    };

    const onBlur = () => {
        if (isAddingRowRef?.current) return;
        if (value !== initValue) {
            updateData(row.index, column.id, value);
        }
    };

    const onChange = (e) => {
        const newValue = e.target.value;
        // Allow only positive integers
        if (newValue === '' || /^\d+$/.test(newValue)) {
            setValue(newValue);
            updateData(row.index, column.id, newValue);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setFocusedRow(row.index, 'unitPrice');
        }
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            setFocusedRow(row.index, 'unitPrice');
        }
        if (e.key === 'Tab' && e.shiftKey) {
            // Shift+Tab on first column - stay or go to previous row's itemCode
            e.preventDefault();
            if (row.index > 0) {
                setFocusedRow(row.index - 1, 'itemCode');
            }
        }
    };

    useEffect(() => {
        if (row.index === focusedRow.row && focusedRow.column === 'quantity') {
            inputRef?.current?.focus();
        }
    }, [focusedRow, row.index]);

    useEffect(() => {
        setValue(initValue);
    }, [initValue]);

    return (
        <Box width="110px" sx={compactFieldSx}>
            <NumberField
                field={{ name: column.id, value, onChange, onBlur }}
                customRef={inputRef}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                placeholder="0"
                darkMode={darkMode}
                noPadding
                width="100%"
                customWidth="100%"
            />
        </Box>
    );
};

// Editable Unit Price Cell (Number Input with decimals)
const EditableUnitPriceCell = ({ getValue, row, column, table }) => {
    const initValue = getValue();
    const { darkMode } = useSelector((state) => state.settings);
    const { updateData, focusedRow, setFocusedRow, isAddingRowRef } =
        table.options.meta;
    const [value, setValue] = useState(initValue);
    const inputRef = useRef(null);

    const onFocus = () => {
        setFocusedRow(row.index, 'unitPrice');
    };

    const onBlur = () => {
        if (isAddingRowRef?.current) return;
        if (value !== initValue) {
            updateData(row.index, column.id, value);
        }
    };

    const onChange = (e) => {
        const newValue = e.target.value;
        // Allow decimal numbers (positive and negative)
        if (
            newValue === '' ||
            newValue === '-' ||
            /^-?\d*\.?\d*$/.test(newValue)
        ) {
            setValue(newValue);
            updateData(row.index, column.id, newValue);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setFocusedRow(row.index, 'description');
        }
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            setFocusedRow(row.index, 'description');
        }
        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            setFocusedRow(row.index, 'quantity');
        }
    };

    useEffect(() => {
        if (row.index === focusedRow.row && focusedRow.column === 'unitPrice') {
            inputRef?.current?.focus();
        }
    }, [focusedRow, row.index]);

    useEffect(() => {
        setValue(initValue);
    }, [initValue]);

    return (
        <Box width="100px" sx={compactFieldSx}>
            <NumberField
                field={{ name: column.id, value, onChange, onBlur }}
                customRef={inputRef}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                placeholder="0.00"
                darkMode={darkMode}
                noPadding
                width="100%"
                customWidth="100%"
            />
        </Box>
    );
};

// Editable Description Cell (Text Input)
const EditableDescriptionCell = ({ getValue, row, column, table }) => {
    const initValue = getValue();
    const { darkMode } = useSelector((state) => state.settings);
    const {
        updateData,
        focusedRow,
        setFocusedRow,
        isAddingRowRef,
        duplicateRowIds,
    } = table.options.meta;
    const [value, setValue] = useState(initValue);
    const inputRef = useRef(null);
    const isDuplicate = duplicateRowIds?.has(row.original.id);

    const onFocus = () => {
        setFocusedRow(row.index, 'description');
    };

    const onBlur = () => {
        if (isAddingRowRef?.current) return;
        if (value !== initValue) {
            updateData(row.index, column.id, value);
        }
    };

    const onChange = (e) => {
        const newValue = e.target.value;
        setValue(newValue);
        updateData(row.index, column.id, newValue);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setFocusedRow(row.index, 'itemCode');
        }
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            setFocusedRow(row.index, 'itemCode');
        }
        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            setFocusedRow(row.index, 'unitPrice');
        }
    };

    useEffect(() => {
        if (
            row.index === focusedRow.row &&
            focusedRow.column === 'description'
        ) {
            inputRef?.current?.focus();
        }
    }, [focusedRow, row.index]);

    useEffect(() => {
        setValue(initValue);
    }, [initValue]);

    return (
        <Box width="280px" sx={descriptionFieldSx}>
            <NameField
                field={{ name: column.id, value, onChange, onBlur }}
                customRef={inputRef}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                placeholder="Enter description"
                darkMode={darkMode}
                isInvalid={isDuplicate}
            />
            {isDuplicate && (
                <Text
                    fontSize="xs"
                    color="red.500"
                    mt="-15px"
                    textAlign="right">
                    Duplicate description
                </Text>
            )}
        </Box>
    );
};

// Read-only Line Total Cell (calculated)
const LineTotalCell = ({ row }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const quantity = row.original.quantity;
    const unitPrice = row.original.unitPrice;
    const lineTotal = calculateLineTotal(quantity, unitPrice);

    const displayValue =
        quantity !== '' && unitPrice !== '' ? lineTotal.toFixed(6) : '-';

    return (
        <Box
            px={3}
            py={1}
            borderRadius="md"
            bg={darkMode ? 'dark.tcap' : 'gray.50'}
            color={darkMode ? 'gray.400' : 'gray.600'}
            fontSize="sm"
            minWidth="100px"
            textAlign="right">
            <Text>{displayValue}</Text>
        </Box>
    );
};

export {
    EditableItemCodeCell,
    EditableQuantityCell,
    EditableUnitPriceCell,
    EditableDescriptionCell,
    LineTotalCell,
};
